import { clsx } from "clsx";
import type { FC, ReactElement } from "react";
import { type BaseFieldProps, Field, type WrappedFieldProps } from "redux-form";
import { DateOnlyPicker, DateTimePicker, type DateTimePickerProps } from "widgets";

import { fieldIsInvalid, fieldIsInWarnState } from "@/app-utils/form.ts";
import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.js";

export interface IReactDateSelectFieldProps
  extends Omit<DateTimePickerProps, "value" | "onChange">,
    Partial<WrappedFieldProps> {
  title?: string | ReactElement;
  forceWarnState?: string;
  tooltip?: string;
  handleChange?: (e: string) => void;
  horizontalLayout?: boolean;
}

/**
 * React Select field to be used in context of redux-form
 *
 * created on 1/24/2017
 */
const ReactDateSelectField: FC<IReactDateSelectFieldProps> = (props) => {
  const inputClasses = clsx("form-control", { error: fieldIsInvalid(props) });

  return (
    <FormInputGroup horizontalLayout={props.horizontalLayout}>
      {props.title && <FormLabel>{props.title}</FormLabel>}

      <FormInputWidget tooltip={props.tooltip}>
        {props.withTime ? (
          <DateTimePicker
            ISOFormattedDate={props.input.value}
            onChange={(e) => {
              props.input.onChange(e);
              props.handleChange?.(e);
            }}
            inputClassName={inputClasses}
            isSelectableDate={props.isSelectableDate}
          />
        ) : (
          <DateOnlyPicker
            ISOFormattedDate={props.input.value}
            onChange={(e) => {
              props.input.onChange(e);
              props.handleChange?.(e);
            }}
            disabled={props.disabled}
            inputClassName={inputClasses}
            isSelectableDate={props.isSelectableDate}
          />
        )}
        {fieldIsInvalid(props) && <FormError>{props.meta.error}</FormError>}
        {(!!props.forceWarnState || fieldIsInWarnState(props)) && (
          <FormWarning>{props.forceWarnState || props.meta.warning}</FormWarning>
        )}
      </FormInputWidget>
    </FormInputGroup>
  );
};

export default ReactDateSelectField;

export function ReactDateSelectReduxFormField(props: IReactDateSelectFieldProps & BaseFieldProps) {
  return <Field {...props} component={ReactDateSelectField} />;
}
